import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Chip,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Mail as MailIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  PersonAddAlt1 as FollowIcon,
} from '@mui/icons-material';
import MdcvVerifiedBadge from '../components/icons/MdcvVerifiedBadge';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useAuth } from '../contexts/AuthContext';

interface ProfileData {
  username: string;
  is_verified: boolean;
  claimed_at?: string;
  npi: string;
  entity_type: number;
  name: string;
  credential?: string;
  specialty?: string;
  location?: { city?: string; state?: string; address?: string | null };
  contact?: { phone?: string | null; email?: string | null };
  bio?: string;
  profile_photo?: string | null;
  cover_photo?: string | null;
  current_position?: string | null;
  current_organization?: string | null;
  years_of_experience?: number | null;
  education?: any[];
  experience?: any[];
  certifications?: any[];
  publications?: any[];
  awards?: any[];
  languages?: string[];
  follower_count?: number;
}

const ProfilePage = () => {
  const { rolePrefix, username } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [followBusy, setFollowBusy] = useState(false);
  const { user, getToken } = useAuth();
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
  const printRef = useRef<HTMLDivElement>(null);

  const displayHandle = useMemo(() => {
    if (!username) return '';
    return (rolePrefix ? `${rolePrefix.toLowerCase()}+` : '+') + username.toLowerCase();
  }, [rolePrefix, username]);

  useEffect(() => {
    const load = async () => {
      if (!username) return;
      setLoading(true);
      setError(null);
      try {
        // Resolve via handle-aware endpoint first
        const handle = displayHandle;
        const res = await fetch(`${API_URL}/api/profiles/handle/${encodeURIComponent(handle)}`);
        if (!res.ok) throw new Error(`Profile not found`);
        const data = await res.json();
        setProfile(data);
      } catch (e: any) {
        setError(e.message || 'Failed to load profile');
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [username, rolePrefix]);

  const onShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert('Profile link copied to clipboard');
    } catch {
      // ignore
    }
  };

  const onDownloadPDF = async () => {
    const node = printRef.current;
    if (!node) return;
    const canvas = await html2canvas(node, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const imgWidth = pageWidth - 20;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let y = 10;
    pdf.addImage(imgData, 'PNG', 10, y, imgWidth, imgHeight);
    pdf.save(`${username}-mdcv.pdf`);
  };

  const toggleFollow = async () => {
    if (!user || !profile) return;
    setFollowBusy(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_URL}/api/profiles/${profile.username}/follow`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        const delta = data.following ? 1 : -1;
        setProfile({ ...profile, follower_count: (profile.follower_count || 0) + delta });
      }
    } finally {
      setFollowBusy(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !profile) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h5">{error || 'Profile not found'}</Typography>
      </Container>
    );
  }

  return (
    <Box>
      {/* Cover area */}
      <Box sx={{
        height: 200,
        backgroundColor: 'grey.200',
        backgroundImage: profile.cover_photo ? `url(${profile.cover_photo})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }} />

      <Container maxWidth="lg" sx={{ mt: -8 }}>
        <Card ref={printRef}>
          <CardContent>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' } }}>
              <Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar
                    src={profile.profile_photo || undefined}
                    sx={{ width: 96, height: 96, border: '3px solid white', mt: -6 }}
                  >
                    {profile.name?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight={700}>
                      {profile.name}
                      {profile.is_verified && (
                        <Tooltip title="Verified">
                          <MdcvVerifiedBadge color="secondary" sx={{ ml: 1, verticalAlign: 'middle' }} />
                        </Tooltip>
                      )}
                    </Typography>
                    <Typography color="text.secondary">
                      {profile.current_position || profile.credential || ''}
                      {profile.current_organization ? ` at ${profile.current_organization}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={1} mt={1}>
                      {profile.specialty && <Chip label={profile.specialty} size="small" />}
                      <Chip label={displayHandle} size="small" variant="outlined" />
                    </Stack>
                  </Box>
                </Box>
              </Box>
              <Box>
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <IconButton onClick={onShare} aria-label="Share profile">
                    <ShareIcon />
                  </IconButton>
                  <IconButton onClick={onDownloadPDF} aria-label="Download PDF">
                    <DownloadIcon />
                  </IconButton>
                  {user && (
                    <Button
                      variant="contained"
                      startIcon={<FollowIcon />}
                      onClick={toggleFollow}
                      disabled={followBusy}
                    >
                      Follow
                    </Button>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gap: 16, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, mt: 1 }}>
              <Box>
                {/* About */}
                {profile.bio && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>About</Typography>
                      <Typography color="text.secondary">{profile.bio}</Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Experience */}
                {Array.isArray(profile.experience) && profile.experience.length > 0 && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Experience</Typography>
                      <Stack spacing={2}>
                        {profile.experience.map((exp: any, i: number) => (
                          <Box key={i}>
                            <Typography fontWeight={600}>{exp.title || exp.role} {exp.organization ? `· ${exp.organization}` : ''}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {exp.start} – {exp.end || 'Present'} · {exp.location || ''}
                            </Typography>
                            {Array.isArray(exp.highlights) && (
                              <ul style={{ marginTop: 8 }}>
                                {exp.highlights.map((h: string, j: number) => (
                                  <li key={j}>
                                    <Typography variant="body2">{h}</Typography>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* Education */}
                {Array.isArray(profile.education) && profile.education.length > 0 && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Education</Typography>
                      <Stack spacing={1}>
                        {profile.education.map((ed: any, i: number) => (
                          <Box key={i}>
                            <Typography fontWeight={600}>{ed.school}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              {ed.degree} · {ed.start} – {ed.end}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* Certifications & Awards */}
                {(Array.isArray(profile.certifications) && profile.certifications.length > 0) || (Array.isArray(profile.awards) && profile.awards.length > 0) ? (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Credentials</Typography>
                      <Stack spacing={1}>
                        {profile.certifications?.map((c: any, i: number) => (
                          <Typography key={`cert-${i}`}>• {c.name || c}</Typography>
                        ))}
                        {profile.awards?.map((a: any, i: number) => (
                          <Typography key={`award-${i}`}>• {a.name || a}</Typography>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                ) : null}

                {/* Publications */}
                {Array.isArray(profile.publications) && profile.publications.length > 0 && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Publications</Typography>
                      <Stack spacing={1}>
                        {profile.publications.map((p: any, i: number) => (
                          <Typography key={i}>• {p.title || p}</Typography>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </Box>

              <Box>
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Details</Typography>
                    <Stack spacing={1}>
                      {profile.location?.city && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <LocationIcon color="action" />
                          <Typography>{profile.location.city}, {profile.location.state}</Typography>
                        </Box>
                      )}
                      {profile.contact?.phone && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <PhoneIcon color="action" />
                          <Typography>{profile.contact.phone}</Typography>
                        </Box>
                      )}
                      {profile.contact?.email && (
                        <Box display="flex" alignItems="center" gap={1}>
                          <MailIcon color="action" />
                          <Typography>{profile.contact.email}</Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" color="text.secondary">Followers</Typography>
                      <Typography variant="h6">{profile.follower_count || 0}</Typography>
                      <Typography variant="body2" color="text.secondary">NPI</Typography>
                      <Typography variant="h6">{profile.npi}</Typography>
                    </Stack>
                  </CardContent>
                </Card>

                {Array.isArray(profile.languages) && profile.languages.length > 0 && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Languages</Typography>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {profile.languages.map((l: string, i: number) => (
                          <Chip key={i} label={l} />
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ProfilePage;
